// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import React, { useState, useCallback, useEffect } from 'react';
import { useAppDispatch } from '../../../../store';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { createJob, clearCreateJobState } from '../../../../store/jobManagementSlice';
import styles from './newJob.module.scss';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

interface NewJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  title: string;
  department: string;
  location: string;
  jd_file: File | null;
  jd_text: string;
  role_context: string;
}

// Character limits for form fields
const FIELD_MAX_LENGTHS = {
  title: 256,
  department: 256,
  location: 256
} as const;

interface FormErrors {
  title?: string;
  department?: string;
  location?: string;
  jd?: string;
}

// Reusable character counter component
const CharacterCounter: React.FC<{ current: number; max: number }> = React.memo(({ current, max }) => (
  <div className={styles.characterCount}>
    {current}/{max}
  </div>
));

const NewJobModal: React.FC<NewJobModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const dispatch = useAppDispatch();
  const { createJobLoading, createJobError, createJobSuccess } = useSelector((state: RootState) => state.jobManagement);
  const [jdMode, setJdMode] = useState<'upload' | 'text'>('upload');
  const [dragActive, setDragActive] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    title: '',
    department: '',
    location: '',
    jd_file: null,
    jd_text: '',
    role_context: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});

  // Handle successful job creation
  useEffect(() => {
    if (createJobSuccess) {
      onSuccess();
      handleClose();
    }
  }, [createJobSuccess]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(clearCreateJobState());
    };
  }, [dispatch]);

  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Job title is required';
    } else if (formData.title.length > FIELD_MAX_LENGTHS.title) {
      newErrors.title = `Job title must not exceed ${FIELD_MAX_LENGTHS.title} characters`;
    }

    if (!formData.department.trim()) {
      newErrors.department = 'Department is required';
    } else if (formData.department.length > FIELD_MAX_LENGTHS.department) {
      newErrors.department = `Department must not exceed ${FIELD_MAX_LENGTHS.department} characters`;
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    } else if (formData.location.length > FIELD_MAX_LENGTHS.location) {
      newErrors.location = `Location must not exceed ${FIELD_MAX_LENGTHS.location} characters`;
    }

    // Job description is mandatory - either file upload or text entry
    if (jdMode === 'upload' && !formData.jd_file) {
      newErrors.jd = 'Please upload a job description PDF or switch to text entry';
    } else if (jdMode === 'text' && !formData.jd_text.trim()) {
      newErrors.jd = 'Job description text is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, jdMode]);

  const handleInputChange = useCallback(
    (field: keyof FormData, value: string): void => {
      // Enforce character limits for specific fields
      if (field in FIELD_MAX_LENGTHS) {
        const maxLength = FIELD_MAX_LENGTHS[field as keyof typeof FIELD_MAX_LENGTHS];
        if (value.length > maxLength) {
          return; // Prevent input beyond max length
        }
      }

      setFormData((prev) => ({ ...prev, [field]: value }));

      // Clear error for this field if it exists in FormErrors
      const errorField = field as keyof FormErrors;
      if (errors[errorField]) {
        setErrors((prev) => ({ ...prev, [errorField]: undefined }));
      }
    },
    [errors]
  );

  // Memoized helper function to get character count info
  const getCharacterCount = useCallback(
    (field: keyof typeof FIELD_MAX_LENGTHS) => {
      const current = formData[field].length;
      const max = FIELD_MAX_LENGTHS[field];
      return { current, max, remaining: max - current };
    },
    [formData]
  );

  const handleDrag = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file: File): void => {
    const validTypes = ['application/pdf'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      setErrors((prev) => ({ ...prev, jd: 'Only PDF files are allowed' }));
      return;
    }

    if (file.size > maxSize) {
      setErrors((prev) => ({ ...prev, jd: 'File size must be less than 5MB' }));
      return;
    }

    setFormData((prev) => ({ ...prev, jd_file: file }));
    setErrors((prev) => ({ ...prev, jd: undefined }));
  };

  const handleRemoveFile = (): void => {
    setFormData((prev) => ({ ...prev, jd_file: null }));
    setErrors((prev) => ({ ...prev, jd: undefined }));
  };

  const handleSubmit = async (): Promise<void> => {
    if (!validateForm()) {
      return;
    }

    const payload = {
      title: formData.title,
      department: formData.department,
      location: formData.location,
      job_description: jdMode === 'text' ? formData.jd_text : undefined,
      job_description_file: jdMode === 'upload' ? formData.jd_file || undefined : undefined,
      role_context: formData.role_context.trim() || undefined
    };

    dispatch(createJob(payload));
  };

  const handleClose = (): void => {
    setFormData({
      title: '',
      department: '',
      location: '',
      jd_file: null,
      jd_text: '',
      role_context: ''
    });
    setErrors({});
    setJdMode('upload');
    dispatch(clearCreateJobState());
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div>
            <h2 className={styles.modalTitle}>Create new job</h2>
            <p className={styles.modalSubtitle}>All fields marked * are required</p>
          </div>
          <button className={styles.closeButton} onClick={handleClose}>
            <CloseIcon />
          </button>
        </div>

        <div className={styles.modalBody}>
          {createJobError && <div className={styles.errorBanner}>{createJobError}</div>}

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                JOB TITLE <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                className={`${styles.input} ${errors.title ? styles.inputError : ''}`}
                placeholder="Senior Software Engineer — AI Platform"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                maxLength={FIELD_MAX_LENGTHS.title}
              />
              <CharacterCounter current={formData.title.length} max={FIELD_MAX_LENGTHS.title} />
              {errors.title && <span className={styles.errorText}>{errors.title}</span>}
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                DEPARTMENT / FUNCTIONAL AREA <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                className={`${styles.input} ${errors.department ? styles.inputError : ''}`}
                placeholder="Engineering"
                value={formData.department}
                onChange={(e) => handleInputChange('department', e.target.value)}
                maxLength={FIELD_MAX_LENGTHS.department}
              />
              <CharacterCounter current={formData.department.length} max={FIELD_MAX_LENGTHS.department} />
              {errors.department && <span className={styles.errorText}>{errors.department}</span>}
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                LOCATION <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                className={`${styles.input} ${errors.location ? styles.inputError : ''}`}
                placeholder="Remote"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                maxLength={FIELD_MAX_LENGTHS.location}
              />
              <CharacterCounter current={formData.location.length} max={FIELD_MAX_LENGTHS.location} />
              {errors.location && <span className={styles.errorText}>{errors.location}</span>}
            </div>
          </div>

          <div className={styles.section}>
            <label className={styles.sectionLabel}>
              JOB DESCRIPTION <span className={styles.required}>*</span> — upload PDF or enter text, required
            </label>
            <div className={styles.jdTabs}>
              <button
                type="button"
                className={`${styles.tabButton} ${jdMode === 'upload' ? styles.active : ''}`}
                onClick={() => setJdMode('upload')}
              >
                Upload PDF
              </button>
              <button
                type="button"
                className={`${styles.tabButton} ${jdMode === 'text' ? styles.active : ''}`}
                onClick={() => setJdMode('text')}
              >
                Enter text
              </button>
            </div>

            {jdMode === 'upload' ? (
              <div
                className={`${styles.uploadArea} ${dragActive ? styles.dragActive : ''} ${errors.jd ? styles.uploadError : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                {formData.jd_file ? (
                  <div className={styles.fileUploaded}>
                    <div className={styles.fileInfo}>
                      <p className={styles.fileName}>{formData.jd_file.name}</p>
                      <p className={styles.fileSize}>{(formData.jd_file.size / 1024).toFixed(0)} KB</p>
                    </div>
                    <button className={styles.removeButton} onClick={handleRemoveFile} type="button">
                      Remove
                    </button>
                  </div>
                ) : (
                  <>
                    <CloudUploadIcon className={styles.uploadIcon} />
                    <p className={styles.uploadText}>Drag and drop PDF here or</p>
                    <label className={styles.browseButton}>
                      Browse File
                      <input type="file" accept=".pdf" onChange={handleFileInput} style={{ display: 'none' }} />
                    </label>
                  </>
                )}
              </div>
            ) : (
              <div className={styles.textEntry}>
                <textarea
                  className={`${styles.textarea} ${errors.jd ? styles.inputError : ''}`}
                  rows={6}
                  placeholder="Paste or type the complete job description here..."
                  value={formData.jd_text}
                  onChange={(e) => handleInputChange('jd_text', e.target.value)}
                />
              </div>
            )}
            {errors.jd && <span className={styles.errorText}>{errors.jd}</span>}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              Role context <span className={styles.optional}>(Optional)</span> — what you're really looking for, beyond
              the JD
            </label>
            <textarea
              className={styles.textarea}
              rows={4}
              placeholder="Looking for a hands-on distributed systems engineer with production Kubernetes experience, not just staging. Low ego, high ownership, strong communicator."
              value={formData.role_context}
              onChange={(e) => handleInputChange('role_context', e.target.value)}
            />
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.cancelButton} onClick={handleClose} disabled={createJobLoading}>
            Cancel
          </button>
          <button className={styles.submitButton} onClick={handleSubmit} disabled={createJobLoading}>
            {createJobLoading ? 'Creating...' : 'Save & create job'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewJobModal;
